import { BaseError } from "../BaseError";

export type SerializedDate<T> = {
  [P in keyof T]: T[P] extends Date | null
    ? string | null
    : T[P] extends Date
    ? string
    : T[P] extends Array<infer R>
    ? Array<SerializedDate<R>>
    : T[P] extends object
    ? SerializedDate<T[P]>
    : T[P];
};

export const serializeDate = <T extends Record<string, unknown>>(obj: T): SerializedDate<T> => {
  if (typeof obj !== "object" || Array.isArray(obj) || obj === null) {
    throw new SerializedDateError("This function argument is object only.");
  }

  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (obj[key] instanceof Date) {
      result[key] = (obj[key] as Date).toISOString();
      continue;
    }
    if (Array.isArray(obj[key])) {
      const array = [];
      for (const element of obj[key] as T[]) {
        if (element instanceof Date) {
          array.push(element.toISOString());
        } else if (typeof element === "object" && element !== null) {
          array.push(serializeDate(element));
        } else {
          array.push(element);
        }
      }
      result[key] = array;
      continue;
    }
    if (typeof obj[key] === "object" && obj[key] !== null) {
      result[key] = serializeDate(obj[key] as T);
      continue;
    }
    result[key] = obj[key];
  }
  return result as SerializedDate<T>;
};

export const deserializeDate = <T extends Record<string, unknown>>(obj: SerializedDate<T>): T => {
  if (typeof obj !== "object" || Array.isArray(obj) || obj === null) {
    throw new DeserializedDateError("This function argument is object only.");
  }

  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === "string") {
      const str = obj[key] as string;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(str)) {
        result[key] = new Date(str);
        continue;
      }
    }
    if (Array.isArray(obj[key])) {
      const array = [];
      for (const element of obj[key] as unknown[]) {
        if (typeof element === "object" && element !== null) {
          array.push(deserializeDate(element as SerializedDate<T>));
          continue;
        }
        if (typeof element === "string") {
          const str = element as string;
          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(str)) {
            array.push(new Date(str));
            continue;
          }
        }
        array.push(element);
      }
      result[key] = array;
      continue;
    }
    if (typeof obj[key] === "object" && obj[key] !== null) {
      result[key] = deserializeDate(obj[key] as SerializedDate<T>);
      continue;
    }
    result[key] = obj[key];
  }
  return result as T;
};

export class SerializedDateError extends BaseError {}
export class DeserializedDateError extends BaseError {}
